// Shared registry of department icons so the picker and the cards stay in sync.
import React from 'react';
import {
  Building2, Code, Megaphone, TrendingUp, PoundSterling, Users, Headphones,
  Palette, Factory, Cog, Boxes, Truck, Server, Scale, Package, Stethoscope,
  GraduationCap, ShoppingCart, BarChart3, Wrench, Briefcase, Rocket, Globe,
  Shield, Heart, Wallet, Phone, Coffee, Plane, Sparkles,
} from 'lucide-react';

export const DEPT_ICON_OPTIONS = [
  { key: 'building', Icon: Building2, label: 'Building' },
  { key: 'code', Icon: Code, label: 'Code' },
  { key: 'megaphone', Icon: Megaphone, label: 'Megaphone' },
  { key: 'trending-up', Icon: TrendingUp, label: 'Growth' },
  { key: 'pound', Icon: PoundSterling, label: 'Finance' },
  { key: 'users', Icon: Users, label: 'People' },
  { key: 'headphones', Icon: Headphones, label: 'Support' },
  { key: 'palette', Icon: Palette, label: 'Design' },
  { key: 'factory', Icon: Factory, label: 'Factory' },
  { key: 'cog', Icon: Cog, label: 'Operations' },
  { key: 'boxes', Icon: Boxes, label: 'Inventory' },
  { key: 'truck', Icon: Truck, label: 'Logistics' },
  { key: 'server', Icon: Server, label: 'IT' },
  { key: 'scale', Icon: Scale, label: 'Legal' },
  { key: 'package', Icon: Package, label: 'Product' },
  { key: 'stethoscope', Icon: Stethoscope, label: 'Health' },
  { key: 'graduation', Icon: GraduationCap, label: 'Training' },
  { key: 'cart', Icon: ShoppingCart, label: 'Retail' },
  { key: 'chart', Icon: BarChart3, label: 'Data' },
  { key: 'wrench', Icon: Wrench, label: 'Maintenance' },
  { key: 'briefcase', Icon: Briefcase, label: 'Business' },
  { key: 'rocket', Icon: Rocket, label: 'Growth/Startup' },
  { key: 'globe', Icon: Globe, label: 'Global' },
  { key: 'shield', Icon: Shield, label: 'Security' },
  { key: 'heart', Icon: Heart, label: 'Care' },
  { key: 'wallet', Icon: Wallet, label: 'Payroll' },
  { key: 'phone', Icon: Phone, label: 'Calls' },
  { key: 'coffee', Icon: Coffee, label: 'Hospitality' },
  { key: 'plane', Icon: Plane, label: 'Travel' },
  { key: 'sparkles', Icon: Sparkles, label: 'General' },
];

export const DEPT_ICON_MAP = Object.fromEntries(DEPT_ICON_OPTIONS.map((o) => [o.key, o.Icon]));
export const getDeptIcon = (key) => DEPT_ICON_MAP[key] || Building2;

const DEPT_RULES = [
  { k: ['engineer', 'develop', 'software', 'devops', 'dev', 'programm'], icon: 'code', color: '#6366f1' },
  { k: ['market', 'brand', 'comms', 'communications'], icon: 'megaphone', color: '#ec4899' },
  { k: ['sales', 'revenue', 'business development', 'biz dev', 'account executive'], icon: 'trending-up', color: '#10b981' },
  { k: ['financ', 'account', 'payroll', 'treasur', 'bookkeep'], icon: 'pound', color: '#14b8a6' },
  { k: ['hr', 'human resource', 'people', 'talent', 'recruit', 'personnel'], icon: 'users', color: '#8b5cf6' },
  { k: ['support', 'customer', 'success', 'help desk', 'helpdesk', 'service desk'], icon: 'headphones', color: '#0ea5e9' },
  { k: ['design', 'creative', 'ux', 'ui', 'graphic'], icon: 'palette', color: '#d946ef' },
  { k: ['manufactur', 'production', 'factory', 'assembly'], icon: 'factory', color: '#a16207' },
  { k: ['warehouse', 'inventory', 'stock', 'fulfil'], icon: 'boxes', color: '#eab308' },
  { k: ['logistic', 'delivery', 'fleet', 'transport', 'courier', 'supply'], icon: 'truck', color: '#ca8a04' },
  { k: ['operation', 'ops'], icon: 'cog', color: '#f59e0b' },
  { k: ['it', 'infrastructure', 'systems', 'network', 'security'], icon: 'server', color: '#64748b' },
  { k: ['legal', 'compliance', 'counsel'], icon: 'scale', color: '#78716c' },
  { k: ['product'], icon: 'package', color: '#f97316' },
  { k: ['health', 'medical', 'clinic', 'care', 'nurse', 'pharma'], icon: 'stethoscope', color: '#f43f5e' },
  { k: ['educat', 'train', 'academy', 'school', 'learning'], icon: 'graduation', color: '#3b82f6' },
  { k: ['retail', 'store', 'shop', 'merchand'], icon: 'cart', color: '#ef4444' },
  { k: ['data', 'analytic', 'insight', 'research', 'r&d'], icon: 'chart', color: '#06b6d4' },
  { k: ['facilit', 'maintenance', 'estate', 'repair'], icon: 'wrench', color: '#71717a' },
];
const DEPT_FALLBACK_COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#14b8a6', '#0ea5e9', '#f97316', '#ef4444', '#06b6d4'];
const matchesKeyword = (name, kw) => {
  if (kw.includes(' ')) return name.includes(kw);
  const esc = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp('\\b' + esc).test(name);
};
export const autoDeptVisual = (name) => {
  const n = (name || '').toLowerCase();
  for (const r of DEPT_RULES) {
    if (r.k.some((kw) => matchesKeyword(n, kw))) return { iconKey: r.icon, color: r.color };
  }
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
  return { iconKey: 'building', color: DEPT_FALLBACK_COLORS[h % DEPT_FALLBACK_COLORS.length] };
};
export const resolveDept = (dept = {}) => {
  const auto = autoDeptVisual(dept.name);
  return { Icon: getDeptIcon(dept.icon || auto.iconKey), color: dept.color || auto.color };
};

export const DEPT_COLOR_OPTIONS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#ef4444', '#f97316',
  '#f59e0b', '#eab308', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#64748b', '#78716c', '#a16207',
];

export const DeptAppearancePicker = ({ icon, color, onIcon, onColor }) => {
  const active = color || '#6366f1';
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Icon</p>
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1.5 border border-border rounded-lg">
          {DEPT_ICON_OPTIONS.map(({ key, Icon, label }) => {
            const selected = icon === key;
            return (
              <button
                type="button"
                key={key}
                title={label}
                onClick={() => onIcon(key)}
                className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${selected ? '' : 'hover:bg-secondary'}`}
                style={selected ? { backgroundColor: active + '22', boxShadow: `inset 0 0 0 2px ${active}` } : {}}
              >
                <Icon className="w-4 h-4" style={{ color: selected ? active : 'currentColor' }} />
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-1.5">Colour</p>
        <div className="flex flex-wrap gap-1.5">
          {DEPT_COLOR_OPTIONS.map((c) => (
            <button
              type="button"
              key={c}
              title={c}
              onClick={() => onColor(c)}
              className="w-6 h-6 rounded-full transition-transform hover:scale-110"
              style={{ backgroundColor: c, boxShadow: color === c ? `0 0 0 2px var(--card, #fff), 0 0 0 4px ${c}` : 'none' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
